import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { 
  Loader2, 
  Package, 
  Plus, 
  Edit, 
  Trash2, 
  Check, 
  X,
  ArrowUpDown 
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Item } from '@/app/types/item';
import { SellableUnit, SellableUnitCreate, SellableUnitUpdate } from '@/app/types/sellableUnit';
import {
  listSellableUnits,
  createSellableUnit,
  updateSellableUnit,
  deleteSellableUnit,
} from '@/app/services/sellableUnitService';
import { SellableUnitForm } from './SellableUnitForm';
import { MoreVertical } from 'lucide-react';

interface SellableUnitsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: Item;
}

export function SellableUnitsModal({
  open,
  onOpenChange,
  item,
}: SellableUnitsModalProps) {
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<SellableUnit | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [unitToDelete, setUnitToDelete] = useState<SellableUnit | null>(null);

  // Fetch sellable units when modal opens
  const { data: units, isLoading, error, refetch } = useQuery({
    queryKey: ['sellable-units', item.id],
    queryFn: () => listSellableUnits(item.id, { is_active_only: false }),
    enabled: open,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: SellableUnitCreate) => createSellableUnit(item.id, data),
    onSuccess: () => {
      toast.success('Sellable unit created successfully');
      queryClient.invalidateQueries({ queryKey: ['sellable-units', item.id] });
      setFormOpen(false);
      setSelectedUnit(null);
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Failed to create sellable unit';
      toast.error(message);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ unitId, data }: { unitId: number; data: SellableUnitUpdate }) =>
      updateSellableUnit(unitId, data),
    onSuccess: () => {
      toast.success('Sellable unit updated successfully');
      queryClient.invalidateQueries({ queryKey: ['sellable-units', item.id] });
      setFormOpen(false);
      setSelectedUnit(null);
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Failed to update sellable unit';
      toast.error(message);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (unitId: number) => deleteSellableUnit(unitId),
    onSuccess: () => {
      toast.success('Sellable unit deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['sellable-units', item.id] });
      setDeleteDialogOpen(false);
      setUnitToDelete(null);
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Failed to delete sellable unit';
      toast.error(message);
    },
  });

  const handleAdd = () => {
    setSelectedUnit(null);
    setFormOpen(true);
  };

  const handleEdit = (unit: SellableUnit) => {
    setSelectedUnit(unit);
    setFormOpen(true);
  };

  const handleDelete = (unit: SellableUnit) => {
    setUnitToDelete(unit);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (unitToDelete) {
      deleteMutation.mutate(unitToDelete.id);
    }
  };

  const handleFormSubmit = async (data: SellableUnitCreate | SellableUnitUpdate) => {
    if (selectedUnit) {
      await updateMutation.mutateAsync({ unitId: selectedUnit.id, data: data as SellableUnitUpdate });
    } else {
      await createMutation.mutateAsync(data as SellableUnitCreate);
    }
  };

  const sortedUnits = units?.sort((a, b) => {
    if (a.sort_order !== b.sort_order) {
      return a.sort_order - b.sort_order;
    }
    return a.display_name.localeCompare(b.display_name);
  });

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Sellable Units for {item.name}
            </DialogTitle>
            <DialogDescription>
              Manage pre-defined purchase quantities for mobile apps. These units allow customers to select options like "200g Pouch" or "Family Pack" instead of entering decimal quantities.
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : error ? (
            <Alert variant="destructive">
              <AlertDescription>
                Failed to load sellable units. Please try again.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <div className="space-y-4">
                {/* Action Bar */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">
                      {sortedUnits?.length || 0} Units
                    </Badge>
                    {sortedUnits && sortedUnits.length > 0 && (
                      <Badge variant="outline">
                        {sortedUnits.filter(u => u.is_active).length} Active
                      </Badge>
                    )}
                  </div>
                  <Button onClick={handleAdd} size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Unit
                  </Button>
                </div>

                {/* Units Table */}
                <ScrollArea className="h-[400px] w-full rounded-md border">
                  {sortedUnits && sortedUnits.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Display Name</TableHead>
                          <TableHead>Quantity Multiplier</TableHead>
                          <TableHead className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <ArrowUpDown className="h-3 w-3" />
                              Sort Order
                            </div>
                          </TableHead>
                          <TableHead className="text-center">Status</TableHead>
                          <TableHead className="text-center">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedUnits.map((unit) => (
                          <TableRow key={unit.id}>
                            <TableCell className="font-medium">
                              {unit.display_name}
                            </TableCell>
                            <TableCell>
                              {parseFloat(unit.quantity_multiplier).toFixed(5).replace(/\.?0+$/, '')}
                              {item.base_unit_name && (
                                <span className="text-muted-foreground ml-1">
                                  {item.base_unit_name}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {unit.sort_order}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant={unit.is_active ? 'default' : 'secondary'}>
                                {unit.is_active ? (
                                  <>
                                    <Check className="h-3 w-3 mr-1" />
                                    Active
                                  </>
                                ) : (
                                  <>
                                    <X className="h-3 w-3 mr-1" />
                                    Inactive
                                  </>
                                )}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleEdit(unit)}>
                                    <Edit className="h-4 w-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => handleDelete(unit)}
                                    className="text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                      <Package className="h-12 w-12 mb-4 opacity-50" />
                      <p className="text-lg font-medium">No sellable units defined</p>
                      <p className="text-sm mt-1">Add units to enable mobile app purchase options</p>
                    </div>
                  )}
                </ScrollArea>

                {/* Info Alert */}
                <Alert>
                  <AlertDescription>
                    <strong>Note:</strong> Sellable units are display-only features for mobile apps. 
                    Prices are calculated as: base price × quantity multiplier. The backend stores only 
                    the units; mobile apps handle UI and calculations.
                  </AlertDescription>
                </Alert>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Form Dialog */}
      <SellableUnitForm
        open={formOpen}
        onOpenChange={setFormOpen}
        unit={selectedUnit}
        onSubmit={handleFormSubmit}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sellable Unit</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{unitToDelete?.display_name}"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}